import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, SendHorizonal, ChevronDown, ChevronUp, Check, Clock, X, AlertCircle, FileDown, Package, FileText, Link2, Ban } from 'lucide-react';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  draft: 'bg-gray-500/20 text-gray-400',
  prepared: 'bg-gray-500/20 text-gray-400',
  submitted: 'bg-blue-500/20 text-blue-400',
  sent: 'bg-blue-500/20 text-blue-400',
  under_review: 'bg-yellow-500/20 text-yellow-400',
  conditional_approval: 'bg-amber-500/20 text-amber-400',
  approved: 'bg-emerald-500/20 text-emerald-400',
  declined: 'bg-red-500/20 text-red-400',
  withdrawn: 'bg-gray-500/20 text-gray-500',
  settled: 'bg-green-500/20 text-green-300',
};

const STATUS_ICONS = {
  draft: Clock, prepared: Clock, submitted: SendHorizonal, sent: SendHorizonal,
  under_review: AlertCircle, conditional_approval: AlertCircle,
  approved: Check, declined: X, withdrawn: Ban, settled: Check,
};

const CONDITION_STATUSES = ['open', 'satisfied', 'waived'];

export default function SubmissionsTab({ deal, dealId, onRefresh }) {
  const submissions = deal.submissions || [];
  const [expanded, setExpanded] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ lender_name: '', product_type: '', amount_submitted: deal.amount || '', term_months: deal.term_months || 60, rate_submitted: '', lender_email: '', lender_contact_name: '' });
  const [saving, setSaving] = useState(false);
  const [newCondition, setNewCondition] = useState('');
  const [generatingPack, setGeneratingPack] = useState(null);

  const handleCreate = async () => {
    if (!newForm.lender_name) { toast.error('Lender name required'); return; }
    setSaving(true);
    try {
      const payload = { ...newForm, deal_id: dealId, amount_submitted: Number(newForm.amount_submitted) || 0, term_months: Number(newForm.term_months) || 60, rate_submitted: Number(newForm.rate_submitted) || null };
      const res = await axios.post(`${API}/crm/deals/${dealId}/submissions`, payload);
      toast.success('Submission created');
      setShowNew(false);
      setNewForm({ lender_name: '', product_type: '', amount_submitted: deal.amount || '', term_months: deal.term_months || 60, rate_submitted: '', lender_email: '', lender_contact_name: '' });
      onRefresh();
      setExpanded(res.data.id);
    } catch (e) { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (subId, newStatus) => {
    try {
      await axios.put(`${API}/crm/submissions/${subId}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleAddCondition = async (subId) => {
    if (!newCondition.trim()) return;
    try {
      await axios.post(`${API}/crm/submissions/${subId}/conditions`, { description: newCondition });
      setNewCondition('');
      toast.success('Condition added');
      onRefresh();
    } catch (e) { toast.error('Failed to add condition'); }
  };

  const handleConditionUpdate = async (subId, condId, status) => {
    try {
      await axios.put(`${API}/crm/submissions/${subId}/conditions/${condId}`, { status });
      toast.success(`Condition ${status}`);
      onRefresh();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleGeneratePack = async (subId) => {
    setGeneratingPack(subId);
    try {
      const res = await axios.post(`${API}/crm/submissions/${subId}/generate-pack`);
      toast.success('Submission pack generated');
      onRefresh();
    } catch (e) { toast.error('Failed to generate pack'); }
    finally { setGeneratingPack(null); }
  };

  const openCount = (conditions) => conditions.filter(c => c.status === 'open').length;

  return (
    <div className="max-w-4xl space-y-4" data-testid="submissions-tab">
      {/* Header with pack generation */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-white font-medium flex items-center gap-2">
          <SendHorizonal className="h-5 w-5 text-purple-400" /> Lender Submissions
        </h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowNew(!showNew)} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-submission-btn">
            <Plus className="h-4 w-4 mr-1" />New Submission
          </Button>
        </div>
      </div>

      {/* New submission form */}
      {showNew && (
        <div className="bg-navy-800/50 rounded-xl p-4 border border-purple-500/20 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'lender_name', label: 'Lender Name', required: true },
              { key: 'lender_contact_name', label: 'Contact Name' },
              { key: 'lender_email', label: 'Lender Email' },
              { key: 'product_type', label: 'Product Type', type: 'select', options: ['chattel_mortgage','finance_lease','operating_lease','hire_purchase','rental','commercial_loan'] },
              { key: 'amount_submitted', label: 'Amount ($)' },
              { key: 'term_months', label: 'Term (months)' },
              { key: 'rate_submitted', label: 'Rate (%)' },
            ].map(f => (
              <div key={f.key}>
                <label className="block text-xs text-gray-400 mb-1">{f.label}{f.required && '*'}</label>
                {f.type === 'select' ? (
                  <select value={newForm[f.key]} onChange={e => setNewForm(p => ({...p, [f.key]: e.target.value}))}
                    className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" data-testid={`new-sub-${f.key}`}>
                    <option value="">Select...</option>
                    {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                  </select>
                ) : (
                  <input value={newForm[f.key] || ''} onChange={e => setNewForm(p => ({...p, [f.key]: e.target.value}))}
                    className="w-full bg-navy-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-500 focus:outline-none" data-testid={`new-sub-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setShowNew(false)} variant="ghost" size="sm" className="text-gray-400" data-testid="cancel-new-sub">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-sub-btn">
              {saving ? 'Creating...' : 'Create Submission'}
            </Button>
          </div>
        </div>
      )}

      {submissions.length === 0 && !showNew && (
        <div className="text-center py-8 text-gray-500 text-sm">No submissions yet. Create one to begin the lender submission process.</div>
      )}

      {/* Submission cards */}
      {submissions.map(sub => {
        const isOpen = expanded === sub.id;
        const conditions = sub.conditions || [];
        const timeline = sub.timeline || [];
        const outstanding = openCount(conditions);
        const StatusIcon = STATUS_ICONS[sub.status] || Clock;
        return (
          <div key={sub.id} className="bg-navy-800/50 rounded-xl border border-white/5 overflow-hidden" data-testid={`submission-card-${sub.id}`}>
            {/* Card header */}
            <button onClick={() => setExpanded(isOpen ? null : sub.id)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5" data-testid={`expand-sub-${sub.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1 ${STATUS_COLORS[sub.status] || STATUS_COLORS.draft}`}>
                  <StatusIcon className="h-3 w-3" />
                  {(sub.status || 'draft').replace(/_/g, ' ')}
                </span>
                <span className="text-white font-medium text-sm">{sub.lender_name}</span>
                <span className="text-xs text-gray-500">${(sub.amount_submitted || 0).toLocaleString()} @ {sub.rate_submitted || '—'}%</span>
                {outstanding > 0 && <span className="text-xs text-yellow-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{outstanding} conditions</span>}
                {sub.pack_generated_at && <span className="text-xs text-green-400 flex items-center gap-1"><Package className="h-3 w-3" />Pack ready</span>}
              </div>
              {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
            </button>

            {isOpen && (
              <div className="px-4 pb-4 border-t border-white/5 space-y-5">
                {/* Status actions */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {['prepared', 'submitted','under_review','conditional_approval','approved','declined','withdrawn'].map(s => (
                    <button key={s} onClick={() => handleStatusChange(sub.id, s)} data-testid={`status-btn-${s}`}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${sub.status === s ? 'border-purple-500 text-purple-400 bg-purple-500/10' : 'border-white/10 text-gray-400 hover:border-white/20 hover:text-white'}`}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>

                {/* Generate Pack */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <Button onClick={() => handleGeneratePack(sub.id)} disabled={generatingPack === sub.id} size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`gen-pack-${sub.id}`}>
                    <FileDown className="h-4 w-4 mr-1.5" />
                    {generatingPack === sub.id ? 'Generating...' : 'Generate Pack'}
                  </Button>
                  {sub.pack_generated_at && (
                    <span className="text-xs text-gray-400">
                      Last generated: {new Date(sub.pack_generated_at).toLocaleString()}
                    </span>
                  )}
                  {sub.pack_pdf_url && (
                    <a href={sub.pack_pdf_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid={`pack-pdf-link-${sub.id}`}>
                      <FileText className="h-3 w-3" />PDF
                    </a>
                  )}
                  {sub.pack_zip_url && (
                    <a href={sub.pack_zip_url} target="_blank" rel="noopener noreferrer" className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid={`pack-zip-link-${sub.id}`}>
                      <Package className="h-3 w-3" />ZIP
                    </a>
                  )}
                </div>

                {/* Conditions */}
                <div>
                  <h4 className="text-xs text-gray-400 uppercase tracking-wider mb-2 font-medium">Conditions ({conditions.length})</h4>
                  {conditions.length === 0 && <p className="text-xs text-gray-600 mb-2">No conditions yet.</p>}
                  <div className="space-y-1.5">
                    {conditions.map(c => (
                      <div key={c.id} className="flex items-center gap-2 py-1.5 group" data-testid={`condition-${c.id}`}>
                        {c.status === 'satisfied' ? (
                          <Check className="h-4 w-4 text-green-400 shrink-0" />
                        ) : c.status === 'waived' ? (
                          <X className="h-4 w-4 text-gray-500 shrink-0" />
                        ) : (
                          <Clock className="h-4 w-4 text-yellow-400 shrink-0" />
                        )}
                        <span className={`text-sm flex-1 ${c.status === 'satisfied' ? 'text-gray-500 line-through' : c.status === 'waived' ? 'text-gray-600 line-through italic' : 'text-gray-300'}`}>
                          {c.description}
                        </span>
                        {c.linked_document_name && (
                          <span className="text-xs text-purple-400 flex items-center gap-0.5"><Link2 className="h-3 w-3" />{c.linked_document_name}</span>
                        )}
                        {c.status === 'open' && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <button onClick={() => handleConditionUpdate(sub.id, c.id, 'satisfied')} className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20" data-testid={`satisfy-cond-${c.id}`}>
                              Satisfy
                            </button>
                            <button onClick={() => handleConditionUpdate(sub.id, c.id, 'waived')} className="text-xs px-2 py-0.5 rounded bg-gray-500/10 text-gray-400 hover:bg-gray-500/20" data-testid={`waive-cond-${c.id}`}>
                              Waive
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <input value={newCondition} onChange={e => setNewCondition(e.target.value)} placeholder="Add condition..."
                      onKeyDown={e => e.key === 'Enter' && handleAddCondition(sub.id)}
                      className="flex-1 bg-navy-800 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:border-purple-500 focus:outline-none"
                      data-testid={`add-condition-input-${sub.id}`} />
                    <Button onClick={() => handleAddCondition(sub.id)} size="sm" variant="outline" className="border-white/10 text-gray-300" data-testid={`add-condition-btn-${sub.id}`}>Add</Button>
                  </div>
                </div>

                {/* Timeline */}
                {timeline.length > 0 && (
                  <div>
                    <h4 className="text-xs text-gray-400 uppercase tracking-wider mb-2 font-medium">Timeline</h4>
                    <div className="relative pl-4 border-l border-white/10 space-y-3">
                      {timeline.slice().reverse().map((ev, i) => {
                        const EvIcon = STATUS_ICONS[ev.status] || Clock;
                        return (
                          <div key={i} className="relative flex items-start gap-3" data-testid={`timeline-event-${i}`}>
                            <div className="absolute -left-[22px] w-5 h-5 rounded-full bg-navy-900 border border-white/10 flex items-center justify-center">
                              <EvIcon className="h-3 w-3 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[ev.status] || 'text-gray-400'}`}>{ev.status.replace(/_/g, ' ')}</span>
                                <span className="text-xs text-gray-500">{new Date(ev.timestamp).toLocaleString()}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-0.5">{ev.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
